import React, { useEffect } from 'react'
import { motion } from 'framer-motion'
import Confetti from 'react-confetti'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import CrownAnimation from '../../../../assets/Crown.lottie'
import endClappingSound from '../../../../assets/crowd_applause_cheering.mp3'
import winnerHornSound from '../../../../assets/winner-horn.mp3'
import bugIcon from '../../../../assets/bug-icon.png'
import pointIcon from '../../../../assets/point-icon.png'
import { getFunnyTitle } from '../../../../utils/funnyTitles'

// Define interfaces for the component props
interface Competitor {
  id: string;
  name: string;
  image: string;
  points: number;
  bugs: number;
  role?: string;
  coins?: number;
  // Extra dynamic properties used by other winner components
  [key: string]: unknown;
}

interface WinnerOverlayProps {
  winner: Competitor;
  onReplay: () => void;
  onSeeResults: () => void;
  borderColor?: string;
  useFunnyTitles?: boolean;
}

// ---------------------------------------------------------------------
// WinnerOverlay Component
// Covers the entire race track with a white background and displays
// confetti along with the winner's details and buttons to replay or see
// sprint results.
// ---------------------------------------------------------------------
const WinnerOverlay: React.FC<WinnerOverlayProps> = ({
  winner,
  onReplay,
  onSeeResults,
  borderColor = '#8a2be2', // Add border color prop with default  
  useFunnyTitles = true // New prop to toggle between funny and real titles
}) => {
  // Get the appropriate title based on setting
  const title = getFunnyTitle(winner, useFunnyTitles)

  // Play victory sounds when component mounts
  useEffect(() => {
    const hornAudio = new Audio(winnerHornSound)
    const clappingAudio = new Audio(endClappingSound)

    // Play horn sound first, then clapping
    hornAudio.play()
    clappingAudio.volume = 0.7 // Slightly lower volume for clapping
    clappingAudio.play()

    // Cleanup function to stop sounds if component unmounts
    return () => {
      hornAudio.pause()
      clappingAudio.pause()
    }
  }, [])

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ zIndex: 20, top: 115 }}>
      <Confetti
        width={1200}
        height={980}
        colors={[
          '#0D3B0C', // Dark green
          '#0F6E0B', // Medium dark green
          '#25A721', // Medium green
          '#3DDE39', // Bright green
          '#98FB98', // Pale green
          '#00FF00'  // Matrix green
        ]}
        numberOfPieces={400}
        drawShape={(ctx: CanvasRenderingContext2D) => {
          // Matrix style vertical character/code line
          const height = 12
          const width = 4

          ctx.fillStyle = ctx.strokeStyle

          // Draw vertical line
          ctx.fillRect(-width / 2, -height / 2, width, height)

          // Add a "bit" to make it look more like a character
          const randomBit = Math.random() > 0.5
          if (randomBit) {
            ctx.fillRect(-width / 2, -height / 2 - width, width, width)
          } else {
            ctx.fillRect(-width / 2, height / 2, width, width)
          }
        }}
      />
      <motion.div
        className="p-8 pt-20 flex flex-col items-center w-[600px] border-[8px] border-amber-600 bg-amber-100 rounded-xl"
        style={{
          boxShadow: '3px 3px 2px rgba(0, 0, 0, 0.4)'
        }}
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="relative">
          <div className="absolute w-32 h-32 top-0 transform -translate-y-2/3 -translate-x-4">
            <DotLottieReact
              src={CrownAnimation as string}
              loop={false}
              autoplay={true}
              className="w-full h-full"
            />
          </div>
          <img
            src={winner.image}
            alt={winner.name}
            className="w-24 h-24 object-cover rounded-full border-4 border-solid"
            style={{ borderColor: borderColor }}
          />
        </div>

        <div className="text-center mt-4">
          <h2 className="text-3xl font-bold text-gray-800 caprasimo-regular mb-2">
            {winner.name} Wins!
          </h2>
          <p className="text-xl text-gray-600 caprasimo-regular italic">
            &quot;{title}&quot;
          </p>
        </div>

        {/* Combined bugs and points with icons on one line */}
        <div className="mt-4 flex justify-center items-center space-x-6 caprasimo-regular">
          <div className="flex items-center">
            <img src={bugIcon} alt="Bugs" className="w-6 h-6 mr-2" />
            <span>{winner.bugs} Bugs</span>
          </div>
          <div className="flex items-center">
            <img src={pointIcon} alt="Points" className="w-6 h-6 mr-2" />
            <span>{winner.points} Points</span>
          </div>
        </div>

        <div className="mt-6 flex space-x-4">
          <button
            onClick={onReplay}
            className="px-4 py-2 text-white rounded hover:opacity-90 caprasimo-regular bg-[#a13616]"
          >
            Re-play Race
          </button>
          <button
            onClick={onSeeResults}
            className="px-4 py-2 text-white rounded hover:opacity-90 caprasimo-regular bg-[#2e5b3e]"
          >
            See Sprint Results
          </button>
        </div>
      </motion.div>
    </div>
  )
}

export default WinnerOverlay
